/**
 * The concierge chat socket. It carries no content — only "the chat of this ticket changed" — and
 * the page answers every signal, and every (re)connect, by re-reading the feed. So a signal lost
 * while the socket was down costs nothing: the read after the reconnect brings it.
 */
export const BACKOFF_MS = [1000, 2000, 5000, 10000, 30000];

export type ChatSocketState = 'connected' | 'reconnecting';

interface Handlers {
  onChange: () => void;
  onState: (state: ChatSocketState) => void;
}

const socketUrl = (uuid: string): string => {
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${window.location.host}/api/concierge/ws?token=${encodeURIComponent(uuid)}`;
};

/** @returns a function that closes the socket for good */
export const openChatSocket = (uuid: string, { onChange, onState }: Handlers): (() => void) => {
  let stopped = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let socket: WebSocket | null = null;

  const connect = () => {
    if (stopped) return;
    socket = new WebSocket(socketUrl(uuid));
    socket.onopen = () => {
      attempt = 0;
      onState('connected');
      onChange();
    };
    socket.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(String(event.data));
        if (data?.type === 'ticket' || data?.type === 'message') onChange();
      } catch {
        // not a signal
      }
    };
    socket.onclose = () => {
      if (stopped) return;
      onState('reconnecting');
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      attempt += 1;
      timer = setTimeout(connect, delay);
    };
    socket.onerror = () => socket?.close();
  };

  connect();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    socket?.close();
  };
};
